// Display helpers for the parent tier surfaces. The server's
// getParentBillingSummary is the only source of access. Nothing here
// reimplements decideTier, and a Checkout return query is not a tier.

const BILLING_DAY = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: 'America/New_York'
})

const STRIPE_REDIRECT_HOSTS = new Set([
  'checkout.stripe.com',
  'billing.stripe.com'
])

export function tierAccessLabel(access) {
  if (access === 'free') return 'Free'
  if (access === 'paid') return 'Unlimited'
  if (access === 'complimentary') return 'Unlimited until Nov 15'
  return null
}

// Card and Manage Tiers. Complimentary still says Manage; the modal then
// offers Keep Unlimited.
export function tierActionLabel(access) {
  if (access === 'free') return 'Upgrade'
  if (access === 'paid' || access === 'complimentary') return 'Manage'
  return null
}

export function formatBillingDay(ms) {
  if (typeof ms !== 'number' || !Number.isFinite(ms)) return null
  const date = new Date(ms)
  if (Number.isNaN(date.getTime())) return null
  let parts
  try {
    parts = BILLING_DAY.formatToParts(date)
  } catch (error) {
    return null
  }
  const month = parts.find((part) => part.type === 'month')
  const day = parts.find((part) => part.type === 'day')
  if (!month || !day || !month.value || !day.value) return null
  return `${month.value} ${day.value}`
}

export function tierModalBody(summary) {
  if (!summary) return ''
  if (summary.access === 'free') {
    return '5 sessions a week on Free. Unlimited is $9.99/month plus tax.'
  }
  if (summary.access === 'complimentary') {
    return 'Included until Nov 15. Then $9.99/month plus tax to keep it.'
  }
  if (summary.access === 'paid') {
    const day = formatBillingDay(summary.renewalAt)
    if (!day) return ''
    if (summary.cancelAtPeriodEnd === true) return `Ends ${day}.`
    return `Renews ${day}.`
  }
  return ''
}

// https only, on the hosts the server already allows. Anything else is refused
// so a bad callable payload cannot send the parent off-site.
export function acceptedBillingUrl(value) {
  if (typeof value !== 'string') return null
  const url = value.trim()
  if (!url || url.length > 2048) return null
  let parsed
  try {
    parsed = new URL(url)
  } catch (error) {
    return null
  }
  if (parsed.protocol !== 'https:' || parsed.username || parsed.password) return null
  if (!STRIPE_REDIRECT_HOSTS.has(parsed.hostname)) return null
  return url
}

// Method on a shared object so a test can replace it. Callers must pass a
// URL that acceptedBillingUrl already allowed.
export const billingNavigation = {
  assign(url) {
    window.location.assign(url)
  }
}

export function searchWithoutBillingReturn(search) {
  const raw = typeof search === 'string' ? search : ''
  const params = new URLSearchParams(raw.charAt(0) === '?' ? raw.slice(1) : raw)
  if (!params.has('billing') && !params.has('session_id')) return raw
  params.delete('billing')
  params.delete('session_id')
  const next = params.toString()
  return next ? `?${next}` : ''
}

// True only means "read getParentBillingSummary again". It is not a tier.
export function checkoutReturnShouldRefresh(search, referrer) {
  const raw = typeof search === 'string' ? search : ''
  const params = new URLSearchParams(raw.charAt(0) === '?' ? raw.slice(1) : raw)
  const billing = params.get('billing')
  if (billing === 'success' || billing === 'cancel') return true
  if (typeof referrer !== 'string' || !referrer) return false
  try {
    const host = new URL(referrer).hostname
    return host === 'checkout.stripe.com' || host === 'billing.stripe.com'
  } catch (error) {
    return false
  }
}
